import React, {
  Suspense, useRef, useEffect, useState
} from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { useTheme } from "../context/ThemeContext";

const HeroScene = React.lazy(() => import("./HeroScene"));

const reveal = {
  hidden: { y: "110%" },
  show:   { y: "0%", transition: { duration: 1.1, ease: [0.65, 0, 0.35, 1] } }
};

const Hero = () => {
  const { t } = useI18n();
  const { theme } = useTheme();

  const gyroRef     = useRef({ beta: 45, gamma: 0 });

  const [isMobile, setIsMobile]   = useState(false);
  const [gyroAvail, setGyroAvail] = useState(false);

  /* ── Responsive ── */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ── Gyroscope (mobile) ── */
  useEffect(() => {
    if (!isMobile) return;
    const handler = (e) => { gyroRef.current = { beta: e.beta ?? 45, gamma: e.gamma ?? 0 }; };
    if (typeof DeviceOrientationEvent !== "undefined" &&
        typeof DeviceOrientationEvent.requestPermission === "function") {
      DeviceOrientationEvent.requestPermission()
        .then(s => { if (s === "granted") { window.addEventListener("deviceorientation", handler); setGyroAvail(true); } })
        .catch(console.warn);
    } else if (window.DeviceOrientationEvent) {
      window.addEventListener("deviceorientation", handler);
      setGyroAvail(true);
    }
    return () => window.removeEventListener("deviceorientation", handler);
  }, [isMobile]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  /* ──────────────────────────────────────────────────────────
     RENDER
     Hidden slogan sits between the title lines — not on top.
     It uses smaller text and is staggered so it only shows
     when the beam sweeps through (clip-path reveals it).
  ─────────────────────────────────────────────────────────── */
  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden hero-radial mayak-grain"
      data-testid="hero-section"
    >
      {/* Three.js canvas */}
      <div className="absolute inset-0 z-0">
        <Suspense fallback={<div className="w-full h-full bg-mayak-bg" />}>
          <HeroScene />
        </Suspense>
      </div>

      {/* Gradient vignette */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-mayak-bg/30 via-transparent to-mayak-bg" />

      {/* Mobile hint */}
      {isMobile && gyroAvail && (
        <div className="absolute top-24 right-6 z-20 opacity-50 pointer-events-none">
          <span className="text-[9px] uppercase tracking-[0.3em] text-mayak-accent">Cihazı eğin</span>
        </div>
      )}

      {/* Side decoration — real */}
      <div className="hidden lg:flex absolute left-12 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-6">
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-mayak-accent/60 to-transparent" />
        <span className="text-[10px] uppercase tracking-[0.4em] text-mayak-accent [writing-mode:vertical-rl]">
          MAYAK / RISING
        </span>
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-mayak-accent/60 to-transparent" />
      </div>

      {/* ── REAL CONTENT ── */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 pt-32 pb-12 min-h-screen flex flex-col justify-end lg:justify-center">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } } }}
          className="max-w-5xl"
        >
          <div className="reveal-mask mb-6">
            <motion.span variants={reveal} className="block mayak-eyebrow">
              {t("hero.eyebrow")}
            </motion.span>
          </div>

          <h1 className="font-black tracking-[-0.04em] leading-[0.92] text-mayak-text text-5xl sm:text-7xl lg:text-[8rem]">
            <div className="overflow-hidden pb-2">
              <motion.div variants={reveal} className="flex flex-wrap items-baseline gap-x-[0.2em]">
                <span>{t("hero.title1")}</span>
                <span className="mayak-text-gradient italic font-light pr-[0.15em]">{t("hero.title2")}</span>
              </motion.div>
            </div>
            <div className="overflow-hidden">
              <motion.span variants={reveal} className="block">{t("hero.title3")}</motion.span>
            </div>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 max-w-xl text-base sm:text-lg text-mayak-dim leading-relaxed"
            data-testid="hero-tagline"
          >
            {t("hero.tagline")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button onClick={() => scrollTo("contact")} className="magnetic-btn" data-testid="hero-cta-primary">
              {t("hero.cta")}<ArrowRight size={16} />
            </button>
            <button onClick={() => scrollTo("projects")} className="ghost-btn" data-testid="hero-cta-secondary">
              {t("hero.secondary")}
            </button>
          </motion.div>
        </motion.div>

        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          onClick={() => scrollTo("about")}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 lg:left-auto lg:right-12 lg:translate-x-0 flex items-center gap-3 text-mayak-faint hover:text-mayak-accent transition-colors"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[10px] uppercase tracking-[0.4em]">{t("hero.scroll")}</span>
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
            <ArrowDown size={14} />
          </motion.span>
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
